import React from "react";
import { ArrowDown, Sparkles } from "lucide-react";
import CountUp from "@/components/CountUp";

const BLUE = "#1d4ed8";
const BLUE_SOFT = "#eff6ff";
const BLUE_BORDER = "#bfdbfe";

/**
 * HeroPreview, the right-rail card on the editorial hero.
 * Static "your aid breakdown" snapshot that conveys value at a glance:
 * sticker price -> aid -> what you actually pay.
 *
 * Internal reveal choreography is driven by per-element `--aw-delay`
 * values, all expressed relative to the page load so they land on top of
 * the card's own slide-in (which begins at ~1.4s in LandingPage).
 */
export default function HeroPreview() {
  return (
    <div className="relative" data-testid="hero-preview">
      {/* Soft glow behind the card */}
      <div
        aria-hidden
        className="absolute -inset-6 rounded-[28px] blur-2xl opacity-60 -z-10"
        style={{ background: "radial-gradient(60% 60% at 60% 30%, #dbeafe 0%, transparent 70%)" }}
      />
      <div
        className="rounded-2xl bg-white"
        style={{
          border: "1px solid #bfdbfe",
          boxShadow: "0 24px 60px -28px rgba(30,58,138,0.20), 0 2px 6px rgba(30,58,138,0.06)",
        }}>
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between aw-rise" style={{ "--aw-delay": "1.0s" }}>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#22c55e" }} />
            <span className="text-[11px] uppercase tracking-[0.18em] text-gray-400">Your aid summary</span>
          </div>
          <span className="text-[11px] text-gray-400 tabular-nums">Vanderbilt · 2026</span>
        </div>

        <div className="p-5 sm:p-6">
          <div className="flex items-baseline gap-2 aw-rise" style={{ "--aw-delay": "1.1s" }}>
            <div className="text-[11px] uppercase tracking-[0.16em] text-gray-400">Sticker price</div>
          </div>
          <div className="mt-1 font-heading text-3xl text-gray-900 tabular-nums aw-rise" style={{ "--aw-delay": "1.2s" }}>
            <span className="aw-strike" style={{ "--aw-delay": "1.35s" }}>$86,250</span>
          </div>

          <div className="my-4 flex items-center gap-2 text-xs text-gray-500 aw-rise" style={{ "--aw-delay": "1.45s" }}>
            <ArrowDown className="w-3.5 h-3.5" style={{ color: BLUE }} />
            <span>Federal Pell + institutional grants</span>
            <span className="ml-auto tabular-nums font-medium" style={{ color: "#15803d" }}>
              -$61,000
            </span>
          </div>

          <div
            className="rounded-xl px-4 py-4 flex items-end justify-between aw-rise"
            style={{ background: BLUE_SOFT, border: `1px solid ${BLUE_BORDER}`, "--aw-delay": "1.6s" }}>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#1e40af" }}>
                What you actually pay
              </div>
              <CountUp
                value={25250}
                delay={1900}
                duration={900}
                className="font-heading mt-1 text-4xl tabular-nums block"
                style={{ color: BLUE }}
              />
              <div className="text-[11px] mt-1" style={{ color: "#1e40af", opacity: 0.8 }}>
                per year, after grants
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.14em] text-gray-400">You save</div>
              <div className="text-base font-semibold tabular-nums mt-0.5" style={{ color: "#15803d" }}>
                $61,000/yr
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3 text-[12px]">
            <div className="aw-rise" style={{ "--aw-delay": "2.15s" }}><Tiny k="Pell"      v="$7,395"  pos /></div>
            <div className="aw-rise" style={{ "--aw-delay": "2.25s" }}><Tiny k="School aid" v="$53,605" pos /></div>
            <div className="aw-rise" style={{ "--aw-delay": "2.35s" }}><Tiny k="4-yr cost" v="$101K"   muted /></div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between aw-fade" style={{ "--aw-delay": "2.5s" }}>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
            <Sparkles className="w-3 h-3" style={{ color: BLUE }} />
            Live AidWaive estimate
          </span>
          <span className="text-[11px] text-gray-400">Nothing stored</span>
        </div>
      </div>
    </div>
  );
}

function Tiny({ k, v, pos, muted }) {
  return (
    <div className="rounded-lg px-2.5 py-2"
      style={{ background: "#f9fafb", border: "1px solid #c7d2fe" }}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-gray-400">{k}</div>
      <div className="mt-0.5 text-sm font-medium tabular-nums"
        style={{ color: pos ? "#15803d" : muted ? "#6b7280" : "#111928" }}>
        {v}
      </div>
    </div>
  );
}
